import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CustomerAuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('customer_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setCustomer(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Customer auth check failed:', error);
      localStorage.removeItem('customer_token');
      setToken(null);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/customer/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('customer_token', access_token);
    setToken(access_token);
    setCustomer(userData);
    return userData;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/customer/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('customer_token', access_token);
    setToken(access_token);
    setCustomer(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    setToken(null);
    setCustomer(null);
  };

  const getOrderHistory = async () => {
    if (!token) return [];
    try {
      const response = await axios.get(`${API}/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get order history:', error);
      return [];
    }
  };

  const getProfile = async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API}/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get profile:', error);
      return null;
    }
  };

  const updateProfile = async (data) => {
    if (!token) return false;
    try {
      await axios.put(`${API}/customer/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local customer state
      if (data.name) {
        setCustomer(prev => prev ? { ...prev, name: data.name } : prev);
      }
      return true;
    } catch (error) {
      console.error('Failed to update profile:', error);
      return false;
    }
  };

  const value = {
    customer,
    token,
    loading,
    register,
    login,
    logout,
    getOrderHistory,
    getProfile,
    updateProfile,
    isAuthenticated: !!customer
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
}
